import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  adminLoyaltyOffers, adminCreateOffer, adminUpdateOffer, adminDeleteOffer, adminLoyaltyMembers,
  adminToggleMemberSubscriber, adminImportSubscribers, adminLoyaltyClaims, adminBusinesses,
} from "../lib/api";
import { Toolbar, Modal, Field, Check, Card, inputCls } from "./AdminLayout";
import { IconBtn, Badge } from "./ArticlesAdmin";
import { Pencil, Trash2, BadgeCheck, EyeOff } from "lucide-react";
import ImageUpload from "../components/ImageUpload";

const TABS = ["Offers", "Members", "Activity"];
const empty = { business_id: "", title: "", description: "", discount: "", image: "", expires: "", subscriber_only: false, active: true };

export default function LoyaltyAdmin() {
  const [tab, setTab] = useState("Offers");
  return (
    <div>
      <div className="mb-4 flex gap-2">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`loyalty-tab-${t.toLowerCase()}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${tab === t ? "bg-[var(--navy)] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>{t}</button>
        ))}
      </div>
      {tab === "Offers" && <OffersTab />}
      {tab === "Members" && <MembersTab />}
      {tab === "Activity" && <ActivityTab />}
    </div>
  );
}

function OffersTab() {
  const [items, setItems] = useState([]);
  const [biz, setBiz] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const load = () => adminLoyaltyOffers().then(setItems).catch(() => {});
  useEffect(() => { load(); adminBusinesses().then(setBiz).catch(() => {}); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const merchants = biz.filter((b) => b.loyalty);

  const openNew = () => { setEditing(null); setForm({ ...empty, business_id: merchants[0]?.id || "" }); setOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...empty, ...o }); setOpen(true); };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_id || !form.title.trim()) return toast.error("Merchant and title are required.");
    const payload = { business_id: form.business_id, title: form.title, description: form.description, discount: form.discount, image: form.image, expires: form.expires, subscriber_only: form.subscriber_only, active: form.active };
    setBusy(true);
    try {
      if (editing) await adminUpdateOffer(editing.id, payload); else await adminCreateOffer(payload);
      toast.success("Offer saved."); setOpen(false); load();
    } catch { toast.error("Could not save offer."); } finally { setBusy(false); }
  };
  const remove = async (o) => { if (!window.confirm(`Delete "${o.title}"?`)) return; await adminDeleteOffer(o.id); load(); };

  return (
    <div>
      <Toolbar title="Loyalty Club Offers" subtitle="Member offers from participating merchants. Mark a merchant as Loyalty Club in Business Directory first." onAdd={openNew} addLabel="New Offer" />
      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Offer</th><th className="px-4 py-3">Merchant</th><th className="px-4 py-3">Who</th><th className="px-4 py-3 text-right">Actions</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((o) => (
              <tr key={o.id} data-testid={`admin-offer-${o.id}`}>
                <td className="px-4 py-3"><p className="font-semibold text-[var(--navy)]">{o.title}</p><p className="text-xs text-slate-400">{o.discount}</p></td>
                <td className="px-4 py-3 text-slate-600">{o.business_name}</td>
                <td className="px-4 py-3"><div className="flex gap-1">
                  {o.subscriber_only ? <Badge icon={BadgeCheck} cls="bg-amber-100 text-amber-700">Subscribers</Badge> : <span className="text-xs text-slate-500">All members</span>}
                  {!o.active && <Badge icon={EyeOff} cls="bg-slate-100 text-slate-500">Hidden</Badge>}
                </div></td>
                <td className="px-4 py-3"><div className="flex justify-end gap-2">
                  <IconBtn onClick={() => openEdit(o)} testid={`edit-offer-${o.id}`}><Pencil size={15} /></IconBtn>
                  <IconBtn onClick={() => remove(o)} danger testid={`delete-offer-${o.id}`}><Trash2 size={15} /></IconBtn>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {open && (
        <Modal title={editing ? "Edit Offer" : "New Offer"} onClose={() => setOpen(false)} onSubmit={submit} busy={busy}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Merchant" full>
              <select data-testid="offer-merchant" value={form.business_id} onChange={set("business_id")} className={inputCls}>
                <option value="">Select a Loyalty Club merchant…</option>
                {merchants.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="Offer title" full><input data-testid="offer-title" value={form.title} onChange={set("title")} className={inputCls} /></Field>
            <Field label="Discount label"><input data-testid="offer-discount" placeholder="e.g. 20% OFF" value={form.discount} onChange={set("discount")} className={inputCls} /></Field>
            <Field label="Expires (optional)"><input data-testid="offer-expires" type="date" value={form.expires} onChange={set("expires")} className={inputCls} /></Field>
            <Field label="Fine print" full><textarea data-testid="offer-description" rows={2} value={form.description} onChange={set("description")} className={inputCls} /></Field>
            <Field label="Image (defaults to merchant photo)" full><ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testid="offer-image" /></Field>
            <Check label="Subscribers only (newspaper subscriber perk)" checked={form.subscriber_only} onChange={() => setForm((f) => ({ ...f, subscriber_only: !f.subscriber_only }))} testid="offer-subscriber-only" />
            <Check label="Visible to members" checked={form.active} onChange={() => setForm((f) => ({ ...f, active: !f.active }))} testid="offer-active" />
          </div>
        </Modal>
      )}
    </div>
  );
}

function MembersTab() {
  const [data, setData] = useState({ members: [], subscriber_count: 0 });
  const [emails, setEmails] = useState("");
  const load = () => adminLoyaltyMembers().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (m) => { await adminToggleMemberSubscriber(m.id); load(); };
  const importList = async () => {
    if (!emails.trim()) return;
    const r = await adminImportSubscribers(emails);
    toast.success(`${r.imported} subscriber email${r.imported === 1 ? "" : "s"} enrolled.`); setEmails(""); load();
  };
  return (
    <div className="space-y-5">
      <Toolbar title="Members" subtitle={`${data.members.length} members · ${data.subscriber_count} subscriber emails on file`} />
      <Card className="p-4">
        <p className="text-sm font-semibold text-[var(--navy)]">Enroll newspaper subscribers</p>
        <p className="text-xs text-slate-500">Paste print subscriber emails (any format). When they sign up with that email they get subscriber perks automatically. Online subscribers are enrolled automatically when they pay.</p>
        <textarea data-testid="import-subscribers-input" rows={3} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="jane@example.com, bob@example.com" className={`${inputCls} mt-2`} />
        <button onClick={importList} data-testid="import-subscribers-button" className="mt-2 rounded-lg bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white">Enroll subscribers</button>
      </Card>
      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Member</th><th className="px-4 py-3">Member #</th><th className="px-4 py-3">Deals claimed</th><th className="px-4 py-3">Last sign-in</th><th className="px-4 py-3 text-right">Subscriber</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {data.members.map((m) => (
              <tr key={m.id} data-testid={`admin-member-${m.id}`}>
                <td className="px-4 py-3"><p className="font-semibold text-[var(--navy)]">{m.name}</p><p className="text-xs text-slate-400">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p></td>
                <td className="px-4 py-3 font-mono text-xs">{m.member_no}</td>
                <td className="px-4 py-3">{m.claim_count}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{m.last_login ? new Date(m.last_login).toLocaleString() : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => toggle(m)} data-testid={`toggle-subscriber-${m.id}`} className={`rounded-full px-3 py-1 text-xs font-bold ${m.is_subscriber ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{m.is_subscriber ? "Subscriber" : "Member"}</button>
                </td>
              </tr>
            ))}
            {data.members.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">No members yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function ActivityTab() {
  const [claims, setClaims] = useState([]);
  useEffect(() => { adminLoyaltyClaims().then(setClaims).catch(() => {}); }, []);
  return (
    <div>
      <Toolbar title="Claim Activity" subtitle="Every deal claimed by members, newest first" />
      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">When</th><th className="px-4 py-3">Member</th><th className="px-4 py-3">Offer</th><th className="px-4 py-3">Code</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {claims.map((c) => (
              <tr key={c.id} data-testid={`admin-claim-${c.id}`}>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(c.claimed_at).toLocaleString()}</td>
                <td className="px-4 py-3"><p className="font-semibold text-[var(--navy)]">{c.member_name}</p><p className="font-mono text-xs text-slate-400">{c.member_no}{c.is_subscriber ? " · Subscriber" : ""}</p></td>
                <td className="px-4 py-3"><p>{c.offer_title}</p><p className="text-xs text-slate-400">{c.business_name}</p></td>
                <td className="px-4 py-3 font-mono font-bold">{c.code}</td>
              </tr>
            ))}
            {claims.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">No claims yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
